import {
  IUnifiedPickerTabConfig,
  TColor,
  TPickerType,
} from './unifiedColorPicker.types'
import { useEffect, useState } from 'react'
import { StandardColorPicker } from './pickerTypes/StandardColorPicker'
import { Button } from '@noissue-ui-kit/button'
import { ListColorPicker } from './pickerTypes/ListColorPicker'
import { ListWithCategoriesColorPicker } from './pickerTypes/ListWithCategoriesColorPicker'
import { PantoneColorPicker } from './pickerTypes/PantoneColorPicker'

export interface IUnifiedColorPicker {
  tabs: IUnifiedPickerTabConfig[]
  initialColors?: TColor[]
  onSelected: (color: TColor) => void
  onClose?: () => void
  wrapperHeight?: number
  className?: string
}

export function UnifiedColorPicker({
  tabs,
  initialColors,
  onSelected,
  onClose,
  wrapperHeight,
  className,
}: IUnifiedColorPicker) {
  const [activeTabIndex, setActiveTabIndex] = useState<number>(
    Math.max(
      initialColors?.findIndex((x) => x.value),
      0
    ) || 0
  )
  const [selectedColors, setSelectedColors] = useState<TColor[]>(
    initialColors || []
  )
  const [lastSelectedColor, setLastSelectedColor] = useState<TColor>(
    initialColors?.[0] || null
  )

  const mapPickerTypeToComponent: Record<TPickerType, any> = {
    hex: StandardColorPicker,
    list: ListColorPicker,
    categories: ListWithCategoriesColorPicker,
    'pantone-input': PantoneColorPicker,
  }

  const onChange = (color: TColor, index: number) => {
    const selectedColorsCopy = Array.from(selectedColors || [])
    selectedColorsCopy[index] = color
    setSelectedColors(selectedColorsCopy)
    setLastSelectedColor(color)
  }

  useEffect(() => {
    if (lastSelectedColor) {
      onSelected(lastSelectedColor)
    }
  }, [lastSelectedColor])

  useEffect(() => {
    setSelectedColors(initialColors)
    setActiveTabIndex(
      Math.max(
        initialColors?.findIndex((x) => x.value),
        0
      ) || 0
    )
  }, [initialColors])

  return (
    <div
      className={`font-mori flex flex-col max-w-full min-w-full shadow-lg bg-white overflow-hidden ${className}`}
    >
      {tabs.length > 1 && (
        <div className="flex flex-row justify-around w-full">
          {tabs.map((tab, index) => (
            <button
              key={tab.tabLabel}
              className={`w-1/2 cursor-pointer px-[12px] pt-[14px] pb-[10px] leading-[20px] text-xl font-semibold text-center
            ${
              activeTabIndex === index
                ? 'bg-core-white text-acai'
                : 'bg-black-10 text-acai-40'
            }`}
              onClick={() => setActiveTabIndex(index)}
            >
              {tab.tabLabel}
            </button>
          ))}
        </div>
      )}

      <div className="min-w-full p-4">
        {tabs.map((tab, index) => {
          const PickerComponent = mapPickerTypeToComponent[tab.pickerType]
          return (
            activeTabIndex === index && (
              <PickerComponent
                {...tab}
                onChange={(color) => onChange(color, index)}
                activeColor={selectedColors?.[index]}
                wrapperHeight={wrapperHeight}
                key={tab.pickerType}
              />
            )
          )
        })}

        <Button
          size="s"
          colour="pitaya"
          corners="square"
          className="w-full mt-4"
          onClick={() => {
            onSelected(lastSelectedColor)
            if (onClose && typeof onClose === 'function') {
              onClose()
            }
          }}
        >
          Select color
        </Button>
      </div>
    </div>
  )
}
